import {useId, startTransition} from 'react'
import type {Icon} from '@primer/octicons-react'
import {
  AlertIcon,
  CircleIcon,
  CheckCircleFillIcon,
  GitMergeIcon,
  CheckIcon,
  XIcon,
  ExclamationIcon,
} from '@primer/octicons-react'
import CircleOcticon from '@github-ui/circle-octicon'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {Label, type LabelColorOptions, NavList, Spinner} from '@primer/react'

import styles from './StackSection.module.css'
import {MergeBoxExpandable} from './common/MergeBoxExpandable'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import {CenteredLoadingSpinner} from './common/CenteredLoadingSpinner'
import {HEADER_ICON_SIZE} from '../../constants'
import type {
  StackSectionStatusType,
  StackSectionEntryType,
  StackSectionEntryStatus,
} from '../../helpers/merge-box-status-calculator/stack-section-status'
import {useStackRebaseStatus, type StackRebasePullRequestStatus} from '../../hooks/mutations/use-stack-rebase-mutation'
import type {AliveChannels as MergeBoxAliveChannels} from '../../page-data/payloads/merge-box'
import type {AliveChannels as StatusChecksAliveChannels} from '../../page-data/payloads/status-checks'
import {useMergeabilityLiveUpdates} from '../../hooks/use-mergeability-live-updates'
import {useStackMergeabilityQuery} from '../../page-data/loaders/use-stack-mergeability-page-data'
import {useMergeMethodContext} from '../../contexts/MergeMethodContext'

export function StackSection({
  pullRequestId,
  status,
  entries,
}: {
  pullRequestId: string
  status: StackSectionStatusType
  entries: StackSectionEntryType[]
}) {
  const stackSectionAriaId = useId()
  const [stackExpanded, setStackExpanded] = useSessionStorage<boolean>(
    `${pullRequestId}:stackExpanded`,
    status !== 'PASSED',
  )
  const {isRebasing, status: rebaseState} = useStackRebaseStatus()

  const otherEntriesToMerge = entries.filter(e => e.position === 'BEFORE' && e.pull.state !== 'MERGED')

  let title
  let subtitle
  if (isRebasing) {
    title = 'Rebasing stack…'

    if (rebaseState) {
      const count = Object.values(rebaseState).length
      subtitle = `Rebasing ${count} pull request${count === 1 ? '' : 's'}.`
    } else {
      subtitle = 'Starting rebase.'
    }
  } else if (status === 'FAILED' || status === 'BLOCKED') {
    title = 'Unable to merge as stack'
    subtitle = 'Some pull requests in the stack cannot be merged.'
  } else if (status === 'PASSED') {
    title = 'Ready to merge as stack'
    const count = otherEntriesToMerge.length
    if (count === 0) {
      subtitle = 'This is the first unmerged pull request in the stack.'
    } else {
      subtitle = `Merging this pull request will also merge ${count} pull request${count === 1 ? '' : 's'} below it.`
    }
  } else {
    title = 'Could not determine stack status'
    subtitle = 'Refresh the page to try again.'
  }

  return (
    <section
      aria-label="Stacked pull requests"
      aria-describedby={stackSectionAriaId}
      className="border-bottom color-border-subtle"
    >
      <MergeBoxSectionHeader
        headerId={stackSectionAriaId}
        title={title}
        subtitle={subtitle}
        icon={
          isRebasing ? (
            <CenteredLoadingSpinner className={styles.StackSectionLoadingSpinner} />
          ) : (
            <StatusIcon status={status} />
          )
        }
        expandableProps={{
          ariaLabel: stackExpanded ? 'Collapse stack details' : 'Expand stack details',
          isExpanded: stackExpanded,
          onToggle: () => {
            setStackExpanded(!stackExpanded)
          },
        }}
      />
      <MergeBoxExpandable isExpanded={stackExpanded}>
        <NavList className="tmp-m-3">
          {entries.map(e => (
            <NavList.Item key={e.pull.number} href={e.pull.url} aria-current={e.position === 'CURRENT'}>
              <EntryListItem entry={e} rebaseState={rebaseState[e.pull.number]} />
            </NavList.Item>
          ))}
        </NavList>
      </MergeBoxExpandable>
    </section>
  )
}

function EntryListItem({
  entry,
  rebaseState,
}: {
  entry: StackSectionEntryType
  rebaseState?: StackRebasePullRequestStatus
}) {
  return (
    <div className="d-flex flex-justify-between">
      <div>
        <EntryStatusIcon entry={entry} rebaseState={rebaseState} />{' '}
        <span className={entry.position === 'AFTER' ? 'fgColor-muted' : ''}>{entry.pull.title}</span>{' '}
        <span className="fgColor-muted">#{entry.pull.number}</span>
      </div>
      <EntryStatusLabel entry={entry} rebaseState={rebaseState} />

      {!!entry.channels && <EntryMergeabilityUpdateListener basePageDataUrl={entry.pull.url} {...entry.channels} />}
    </div>
  )
}

function EntryMergeabilityUpdateListener({
  basePageDataUrl,
  ...channels
}: {
  basePageDataUrl: string
} & MergeBoxAliveChannels &
  StatusChecksAliveChannels) {
  const {mergeMethod} = useMergeMethodContext()
  const {refetch} = useStackMergeabilityQuery({basePageDataUrl, mergeMethod, enabled: false})
  useMergeabilityLiveUpdates({
    channels,
    refetchQuery: () => {
      startTransition(() => {
        refetch()
      })
    },
    throttleTime: 5000,
  })
  return null
}

type EntryIconInfo = {type: 'icon'; icon: Icon; className: string; ariaLabel: string} | {type: 'spinner'}

type EntryStatusInfo = {
  label: {variant: LabelColorOptions; text: string} | null
  icon: EntryIconInfo
}

const ENTRY_STATUS_INFO: Record<StackSectionEntryStatus, EntryStatusInfo> = {
  IRRELEVANT_MERGED: {
    label: {variant: 'done', text: 'Merged'},
    icon: {type: 'icon', icon: GitMergeIcon, className: 'color-fg-done', ariaLabel: 'Already merged'},
  },
  IRRELEVANT_AFTER: {
    label: null,
    icon: {
      type: 'icon',
      icon: CircleIcon,
      className: 'color-fg-muted',
      ariaLabel: 'Will not merge: higher up the stack',
    },
  },
  BLOCKED_STACK: {
    label: {variant: 'attention', text: 'Needs rebase'},
    icon: {type: 'icon', icon: AlertIcon, className: 'color-fg-attention', ariaLabel: 'Cannot merge'},
  },
  OK: {
    label: {variant: 'success', text: 'Ready to merge'},
    icon: {type: 'icon', icon: CheckCircleFillIcon, className: 'color-fg-success', ariaLabel: 'Ready to merge'},
  },
  LOADING_MERGE_STATE: {
    label: null,
    icon: {type: 'spinner'},
  },
  ERROR_MERGE_STATE: {
    label: {variant: 'attention', text: 'Error loading data'},
    icon: {
      type: 'icon',
      icon: AlertIcon,
      className: 'color-fg-attention',
      ariaLabel: 'Cannot load mergeability data',
    },
  },
  BLOCKED_MERGE_STATE: {
    label: {variant: 'attention', text: 'Failing rules'},
    icon: {type: 'icon', icon: AlertIcon, className: 'color-fg-attention', ariaLabel: 'Cannot merge'},
  },
  UNKNOWN_MERGE_STATE: {
    label: {variant: 'default', text: 'Checking ability to merge automatically'},
    icon: {type: 'spinner'},
  },
}

function EntryStatusLabel({
  entry,
  rebaseState,
}: {
  entry: StackSectionEntryType
  rebaseState?: StackRebasePullRequestStatus
}) {
  // Rebasing
  if (rebaseState === 'SUCCEEDED') {
    return <Label variant="success">Rebased</Label>
  } else if (rebaseState === 'CONFLICTED') {
    return <Label variant="danger">Merge conflicts</Label>
  } else if (rebaseState === 'FAILED') {
    return <Label variant="danger">Rebase failed</Label>
  }

  const {label} = ENTRY_STATUS_INFO[entry.status]
  if (!label) return null
  return <Label variant={label.variant}>{label.text}</Label>
}

function EntryStatusIcon({
  entry,
  rebaseState,
}: {
  entry: StackSectionEntryType
  rebaseState?: StackRebasePullRequestStatus
}) {
  // Rebasing
  if (rebaseState === 'PENDING') {
    return <Spinner size="small" />
  }

  const {icon} = ENTRY_STATUS_INFO[entry.status]
  if (icon.type === 'spinner') {
    return <Spinner size="small" />
  }
  const EntryIcon = icon.icon
  return <EntryIcon size={16} className={icon.className} aria-label={icon.ariaLabel} />
}

function StatusIcon({status}: {status: StackSectionStatusType}) {
  let className
  let Icon
  switch (status) {
    case 'BLOCKED':
    case 'FAILED':
      Icon = XIcon
      className = 'bgColor-danger-emphasis fgColor-onEmphasis'
      break
    case 'PASSED':
      Icon = CheckIcon
      className = 'bgColor-success-emphasis fgColor-onEmphasis'
      break
    case 'UNKNOWN':
    default:
      Icon = ExclamationIcon
      className = 'bgColor-muted fgColor-onEmphasis'
      break
  }

  return <CircleOcticon className={className} size={HEADER_ICON_SIZE} icon={() => <Icon size={16} />} />
}
